import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  clearPortrait,
  cropPortrait,
  livePictureURL,
  describePortrait,
  generatePortrait,
  setPortrait,
  uploadPortrait,
  type Character,
  type CharacterForm,
  type Characters,
  type DrawPrice,
} from "../api";
import LivePortrait from "../components/LivePortrait";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import PictureSheet from "./PictureSheet";

/**
 * PortraitButton is a character's face, and the way to give them one
 * (ADR-0039).
 *
 * A portrait is not decoration here. Every picture of a scene this character
 * appears in is drawn *from* it, which is the only thing that keeps them
 * looking like the same person from chapter to chapter — so a book cannot be
 * illustrated until every character has one. That is why an empty one is a
 * dashed outline asking to be filled rather than a neutral placeholder.
 */
export default function PortraitButton({
  bookID,
  character,
  form,
  canDescribe,
  prices,
  onChanged,
}: {
  bookID: string;
  character: Character;
  /**
   * One of the character's forms (ADR-0159): the button is that form's face,
   * described and attached as the form. Drawing, uploading and cropping are
   * the character's calls, which store a picture and attach nothing.
   */
  form?: CharacterForm;
  /** Whether this account may spend on describing and drawing (`book.augment`). */
  canDescribe: boolean;
  /** What one portrait costs at each quality the server offers (ADR-0086). */
  prices: DrawPrice[];
  /** The roster as it stands after a change; every portrait call returns it. */
  onChanged: (roster: Characters) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // Whose face this is: the character's own, or one of their forms'.
  const name = form ? form.name : character.name;
  const portrait = form ? form.portrait : character.portrait;

  const clear = useMutation({
    mutationFn: () => clearPortrait(bookID, character.id, form?.id),
    onSuccess: onChanged,
  });

  // A form is never drawn by a batch (ADR-0159): its face is the author's.
  const drawing = !form && character.portrait_batch?.status === "pending";

  return (
    <>
      <div className="shrink-0">
        <div className="relative">
          <button
            type="button"
            data-testid={form ? "form-portrait" : "portrait"}
            data-has-portrait={portrait ? "true" : undefined}
            aria-label={portrait ? t.portraits.change(name) : t.portraits.add(name)}
            onClick={() => setOpen(true)}
            className={
              portrait
                ? "block size-14 overflow-hidden rounded-card border border-line transition-opacity hover:opacity-80"
                : "grid size-14 place-items-center rounded-card border border-dashed border-amber-400 text-[0.6rem] leading-tight text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/40"
            }
          >
            {portrait ? (
              <img
                src={portrait.url}
                alt=""
                width={portrait.width}
                height={portrait.height}
                loading="lazy"
                className="size-full object-cover"
              />
            ) : (
              <span data-testid="portrait-missing">{t.portraits.none}</span>
            )}
          </button>
          {/* A batch is drawing this face (ADR-0049). Over the old one, which
            stays until the new one is attached. */}
          {drawing && (
            <span
              data-testid="portrait-drawing"
              aria-live="polite"
              className="pointer-events-none absolute inset-0 grid place-items-center rounded-card border border-dashed border-sage bg-surface text-center text-[0.6rem] leading-tight text-sage-deep"
            >
              <span className="animate-pulse">{t.portraits.batchDrawing}</span>
            </span>
          )}
        </div>
        {portrait && (
          <button
            type="button"
            data-testid="portrait-remove"
            disabled={clear.isPending}
            onClick={() => clear.mutate()}
            className="mt-1 w-14 text-center text-[0.65rem] text-muted transition-colors hover:text-ink disabled:opacity-40"
          >
            {t.portraits.remove}
          </button>
        )}
        {/* Removing a face closes the illustration gate (ADR-0039). A silent
            failure leaves the author believing it did. */}
        <MutationError
          className="mt-1 w-14 text-[0.65rem]"
          testId="portrait-remove-error"
          error={clear.error}
          fallback={t.portraits.removeFailed}
        />
      </div>

      {open && (
        <PictureSheet
          kind="portrait"
          title={t.portraits.title(name)}
          /* What the picture is of: the character as the roster has them, or
             the form as the author wrote it. The studio quotes it so the
             author can see what the model was told. */
          subject={
            form
              ? [t.characters.forms.of(form.name, character.name), form.appearance]
                  .filter(Boolean)
                  .join(" · ")
              : [character.role, character.description].filter(Boolean).join(" · ")
          }
          prices={prices}
          canDescribe={canDescribe}
          onDescribe={(v) => describePortrait(bookID, character.id, v, form?.id)}
          onGenerate={(prompt, _shape, _cast, _beside, quality) =>
            generatePortrait(bookID, character.id, prompt, quality)
          }
          onUpload={(file) => uploadPortrait(bookID, character.id, file)}
          onCrop={(key, region) => cropPortrait(bookID, character.id, key, region)}
          onUse={async (candidate) => {
            const roster = await setPortrait(
              bookID,
              character.id,
              {
                object_key: candidate.object_key,
                prompt: candidate.prompt,
                source: candidate.prompt ? "generated" : "upload",
                quality: candidate.quality,
              },
              form?.id,
            );
            onChanged(roster);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
          current={portrait}
          live={
            !form &&
            character.portrait &&
            character.live_portrait && (
              <LivePortrait
                size={160}
                className="rounded-card border border-line"
                fallback={character.portrait.url}
                sources={{
                  image: livePictureURL(bookID, character.id, "image", character.live_portrait.key),
                  blink: character.live_portrait.blink_url
                    ? livePictureURL(bookID, character.id, "blink", character.live_portrait.key)
                    : undefined,
                  plate: livePictureURL(bookID, character.id, "plate", character.live_portrait.key),
                  depth: livePictureURL(bookID, character.id, "depth", character.live_portrait.key),
                }}
              />
            )
          }
        />
      )}
    </>
  );
}
